import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import {
  ArrowLeft,
  Copy,
  Check,
  UploadCloud,
  FileText,
  X,
  Landmark,
} from 'lucide-react';
import useAuth from '../../../hooks/useAuth';
import useCobroStore from '../../../store/cobro';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Skeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import {
  formatCurrency,
  formatDate,
  CHARGE_MODE_LABELS,
} from '../../adminView/cobro/utils/cobroHelpers';
import {
  amountDueOf,
  hasValidPrecision,
  paidAmountOf,
  parseAmountInput,
} from '../../adminView/cobro/utils/payments';

const ACCOUNT_TYPE_LABELS = {
  corriente: 'Cuenta Corriente',
  vista: 'Cuenta Vista',
  ahorro: 'Cuenta de Ahorro',
  rut: 'Cuenta RUT',
};

const PROOF_ACCEPT = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_BYTES = 10 * 1024 * 1024;

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const prettySize = (b) =>
  b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

// Fila de un dato bancario con botón de copiar.
const BankRow = ({ label, value }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('No se pudo copiar.'));
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-b-0">
      <span className="text-sm text-fg-muted">{label}</span>
      <span className="flex items-center gap-2 min-w-0">
        <span className="text-sm font-medium text-fg truncate">{value}</span>
        <button
          type="button"
          onClick={copy}
          className="text-fg-muted hover:text-brand-600 shrink-0"
          aria-label={`${t('Copiar')} ${label}`}
        >
          {copied ? <Check className="w-4 h-4 text-status-success" /> : <Copy className="w-4 h-4" />}
        </button>
      </span>
    </div>
  );
};

const PaymentTransfer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const { getMyNote, fetchPaymentMethods, submitTransfer, submittingTransfer } =
    useCobroStore((s) => ({
      getMyNote: s.getMyNote,
      fetchPaymentMethods: s.fetchPaymentMethods,
      submitTransfer: s.submitTransfer,
      submittingTransfer: s.submittingTransfer,
    }));

  const [note, setNote] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  // Monto declarado por el comercio: puede ser un abono menor al saldo.
  const [amount, setAmount] = useState('');

  const backToMethods = () => navigate(`/home/pagos/${id}/pagar`);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getMyNote(id), fetchPaymentMethods(id)])
      .then(([noteData, methodsData]) => {
        if (!active) return;
        setNote(noteData);
        setAmount(String(amountDueOf(noteData)));
        // Si ya está resuelta / en revisión, no tiene sentido informar de nuevo.
        if (noteData?.paymentStatus === 'in_review') {
          navigate(`/home/pagos/${id}/resultado`, { replace: true });
          return;
        }
        if (
          noteData?.paymentStatus === 'paid' ||
          noteData?.paymentStatus === 'cancelled' ||
          amountDueOf(noteData) <= 0
        ) {
          navigate(`/home/pagos/${id}`, { replace: true });
          return;
        }
        const transfer = (methodsData?.methods ?? []).find(
          (m) => m.type === 'manual_transfer',
        );
        if (!transfer?.bank) {
          toast.error(t('La transferencia manual no está disponible para esta nota.'));
          backToMethods();
          return;
        }
        setBank(transfer.bank);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ?? t('No se pudo cargar la transferencia.'),
        );
        backToMethods();
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const userEmail = user?.email;
  // Depende del email, no del objeto `user`: si el provider entrega un objeto nuevo
  // en cada render, `[user]` re-dispara el efecto sin fin (setEmail con el mismo valor).
  useEffect(() => {
    if (userEmail) setEmail(userEmail);
  }, [userEmail]);

  const accountTypeLabel = useMemo(
    () => (bank?.accountType ? t(ACCOUNT_TYPE_LABELS[bank.accountType] ?? bank.accountType) : ''),
    [bank, t],
  );

  const pickFile = (f) => {
    if (!f) return;
    if (!PROOF_ACCEPT.includes(f.type)) {
      toast.error(t('El comprobante debe ser una imagen (JPG/PNG) o un PDF.'));
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(t('El comprobante no puede superar los 10 MB.'));
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    const trimmed = email.trim();
    const currency = note?.currency ?? 'CLP';
    const due = amountDueOf(note);
    const amountValue = parseAmountInput(amount);
    if (!emailOk(trimmed)) {
      toast.error(t('Ingresa un correo válido para enviarte el comprobante.'));
      return;
    }
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error(t('El monto transferido debe ser mayor que cero.'));
      return;
    }
    if (!hasValidPrecision(amountValue, currency)) {
      toast.error(
        currency === 'CLP'
          ? t('El monto debe ser un número entero para {{currency}}.', { currency })
          : t('El monto debe tener hasta 2 decimales.'),
      );
      return;
    }
    if (amountValue > due) {
      toast.error(
        t('El monto transferido no puede superar el saldo ({{amount}}).', {
          amount: formatCurrency(due, currency),
        }),
      );
      return;
    }
    if (!file) {
      toast.error(t('Adjunta el comprobante de la transferencia.'));
      return;
    }
    try {
      await submitTransfer({ noteId: id, email: trimmed, file, amount: amountValue });
      toast.success(t('Transferencia informada. Tu operador la revisará.'));
      navigate(`/home/pagos/${id}/resultado`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? t('No se pudo informar la transferencia.'),
      );
    }
  };

  if (loading || !note || !bank) {
    return (
      <div className="bill bill-page" style={{ padding: 16 }} role="status" aria-live="polite">
        <Skeleton className="h-4 w-32" style={{ marginBottom: '1rem' }} />
        <div className="bill-card bill-card--pad">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-40" style={{ marginTop: '0.5rem' }} />
        </div>
        <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
          <Skeleton className="h-40 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  const cur = note.currency ?? 'CLP';
  const amountDue = amountDueOf(note);
  const amountPaid = paidAmountOf(note);

  return (
    <div className="bill bill-page" style={{ padding: 16 }}>
      <button type="button" className="bill-back" onClick={backToMethods}>
        <ArrowLeft className="w-[18px] h-[18px]" />
        {t('Cambiar método de pago')}
      </button>

      {/* Monto a transferir */}
      <div className="bill-card bill-card--pad">
        <div className="bill-doc-head">
          <div>
            <div className="bill-doc-head__title">
              <h2>{t('Transferencia · nota #')}{note.folio ?? '—'}</h2>
            </div>
            <div className="bill-doc-head__meta">
              {t(CHARGE_MODE_LABELS[note.chargeMode] ?? note.chargeMode)} · {t('Periodo:')}{' '}
              <strong>{formatDate(note.periodStart)}</strong> →{' '}
              <strong>{formatDate(note.periodEnd)}</strong>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="bill-summary__label">{t('Saldo a pagar')}</div>
            <div className="bill-summary__value bill-summary__value--accent">
              {formatCurrency(amountDue, cur)}
            </div>
            {amountPaid > 0 && (
              <div className="bill-hint">
                {t('Total de la nota')}: {formatCurrency(note.totalAmount, cur)} ·{' '}
                {t('Pagado')}: {formatCurrency(amountPaid, cur)}
              </div>
            )}
          </div>
        </div>

        <div className="bill-field" style={{ maxWidth: '18rem', marginTop: '1rem' }}>
          <label className="bill-label" htmlFor="transfer-amount">
            {t('Monto transferido')} <span className="text-status-error">*</span>
          </label>
          <Input
            id="transfer-amount"
            type="number"
            min="1"
            step={cur === 'CLP' ? '1' : '0.01'}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <span className="bill-hint">
            {t('Puedes abonar menos que el saldo. El máximo es {{amount}}.', {
              amount: formatCurrency(amountDue, cur),
            })}
          </span>
        </div>
      </div>

      {/* Datos bancarios del operador */}
      <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
        <div className="flex items-center gap-2" style={{ marginBottom: '0.5rem' }}>
          <Landmark className="w-5 h-5 text-fg-muted" aria-hidden />
          <h3 className="bill-card__title" style={{ margin: 0 }}>
            {t('Datos para transferir')}
          </h3>
        </div>
        <div>
          <BankRow label={t('Banco')} value={bank.bankName} />
          <BankRow label={t('Tipo de cuenta')} value={accountTypeLabel} />
          <BankRow label={t('N° de cuenta')} value={bank.accountNumber} />
          <BankRow label={t('Titular')} value={bank.holderName} />
          <BankRow label={t('RUT')} value={bank.holderRut} />
        </div>
      </div>

      {/* Comprobante (obligatorio) + correo */}
      <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
        <h3 className="bill-card__title" style={{ marginBottom: '0.875rem' }}>
          {t('Adjunta tu comprobante')}
        </h3>

        {!file ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-colors ${
              dragOver ? 'border-brand-500 bg-brand-50' : 'border-border bg-bg-subtle hover:bg-bg-muted'
            }`}
            style={{ padding: '2rem 1rem' }}
          >
            <UploadCloud className="w-7 h-7 text-fg-muted" aria-hidden />
            <span className="text-sm text-fg">
              {t('Arrastra el comprobante o haz clic para seleccionarlo')}
            </span>
            <span className="text-xs text-fg-subtle">{t('JPG, PNG o PDF, hasta 10 MB')}</span>
          </button>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-bg" style={{ padding: '0.875rem 1rem' }}>
            <FileText className="w-5 h-5 text-brand-600 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-fg truncate">{file.name}</span>
              <span className="block text-xs text-fg-subtle">{prettySize(file.size)}</span>
            </span>
            <button
              type="button"
              onClick={() => setFile(null)}
              className="text-fg-muted hover:text-status-error shrink-0"
              aria-label={t('Quitar comprobante')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          className="hidden"
          aria-label={t('Seleccionar comprobante')}
          onChange={(e) => pickFile(e.target.files?.[0])}
        />

        <div className="bill-field" style={{ marginTop: '1rem' }}>
          <label className="bill-label" htmlFor="transfer-email">
            {t('Correo para el comprobante')}
          </label>
          <Input
            id="transfer-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tucorreo@empresa.cl"
          />
        </div>

        <div
          className="flex items-start gap-2.5 p-3 rounded-lg bg-status-info-soft"
          style={{ marginTop: '1rem' }}
        >
          <Landmark
            className="w-5 h-5 shrink-0"
            style={{ color: 'rgb(var(--status-info))' }}
            aria-hidden
          />
          <p className="text-sm text-fg-muted" style={{ margin: 0 }}>
            {t('Tu pago quedará')} <strong className="text-fg">{t('en revisión')}</strong> {t('hasta que tu operador confirme la transferencia. Te avisaremos cuando esté confirmada.')}
          </p>
        </div>

        <div className="flex justify-end gap-2 flex-wrap" style={{ marginTop: '1rem' }}>
          <Button variant="outline" onClick={backToMethods}>
            {t('Cancelar')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={submittingTransfer}
            disabled={!file || !Number.isFinite(parseAmountInput(amount)) || parseAmountInput(amount) <= 0}
          >
            {t('Informar transferencia')}
          </Button>
        </div>
      </div>

      {/* Defensa: si por algún motivo no hay datos bancarios. */}
      {!bank.accountNumber && (
        <EmptyState
          icon={<Landmark />}
          title={t('Faltan datos bancarios')}
          description={t('Tu operador no ha cargado todos los datos para transferencia. Escríbele para coordinar el pago.')}
        />
      )}
    </div>
  );
};

export default PaymentTransfer;
